import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { useUser } from '../context/UserContext';
import {
  getProspect, updateProspect, deleteProspect, convertProspectToStudent, convertProspectToLead,
  addProspectCommunication, addProspectFollowUp,
  getProspectDocuments, uploadProspectDocument, deleteProspectDocument,
  createSignatureRequest, getSignatures, initiateCall, scheduleCall, getVoicenterCalls,
  getCourses, createRegistration, API_ORIGIN
} from '../services/api';
import TasksTab from '../components/TasksTab';
import ContextMenu from '../components/ContextMenu';
import TaskFromContextModal from '../components/TaskFromContextModal';

export default function ProspectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { userName } = useUser();
  const [prospect, setProspect] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('info');
  const [documents, setDocuments] = useState<any[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDocType, setUploadDocType] = useState('other');
  const [uploadNotes, setUploadNotes] = useState('');
  const [showCommModal, setShowCommModal] = useState(false);
  const [commForm, setCommForm] = useState({ type: 'phone', subject: '', message: '', sentBy: '', performedBy: '' });
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpForm, setFollowUpForm] = useState({ handler: '', action: '', nextStep: '', performedBy: '' });
  const [editStatus, setEditStatus] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<any>({});
  const [successMsg, setSuccessMsg] = useState('');
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; type: string; item: any } | null>(null);
  const [ctxTaskModal, setCtxTaskModal] = useState<{ sourceType: 'document' | 'communication'; sourceName: string; sourceLink?: string } | null>(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureForm, setSignatureForm] = useState({ documentName: '', message: '' });
  const [signatures, setSignatures] = useState<any[]>([]);
  const [callHistory, setCallHistory] = useState<any[]>([]);
  const [showScheduleCallModal, setShowScheduleCallModal] = useState(false);
  const [scheduleCallForm, setScheduleCallForm] = useState({ scheduledDate: '', scheduledTime: '' });
  const [showRegFormModal, setShowRegFormModal] = useState(false);
  const [regForm, setRegForm] = useState({ courseName: '', tuitionAmount: '', academicYear: '', personalMessage: '', sendMethod: 'both' });
  const [coursesList, setCoursesList] = useState<any[]>([]);

  useEffect(() => {
    if (!id) return;
    getProspect(id).then(res => {
      setProspect(res.data);
      setEditStatus(res.data.status);
    }).catch(() => {});
    getProspectDocuments(id).then(res => setDocuments(res.data)).catch(() => {});
    getSignatures({ entityType: 'prospect', entityId: id }).then(res => setSignatures(res.data)).catch(() => {});
    getVoicenterCalls({ entityType: 'prospect', entityId: id }).then(res => setCallHistory(res.data)).catch(() => {});
    getCourses().then(res => setCoursesList(res.data)).catch(() => {});
  }, [id]);

  if (!prospect) return <div className="page-section"><p>{t('loading')}</p></div>;

  const getName = () => {
    if (lang === 'ar' && prospect.firstNameAr) return `${prospect.firstNameAr} ${prospect.lastNameAr}`;
    return `${prospect.firstName} ${prospect.lastName}`;
  };

  const getStatusText = (status: string) => t(`prospectStatuses.${status}`);
  const getSourceText = (source: string) => t(`sources.${source}`);

  const statusColors: Record<string, string> = {
    initial_contact: 'badge-blue',
    in_process: 'badge-gold',
    documents_submitted: 'badge-yellow',
    waiting_approval: 'badge-gray',
    approved_for_registration: 'badge-green',
    rejected: 'badge-red',
    registered: 'badge-green',
  };

  const docTypeKeys = ['id_card', 'bagrut', 'prev_studies', 'medical', 'passport_photo', 'registration_contract', 'receipt', 'other'];
  const getDocTypeName = (key: string) => t(`docTypes.${key}`);

  const openWhatsApp = () => {
    const phone = prospect.phone.replace(/[^0-9]/g, '');
    window.open(`https://wa.me/972${phone.startsWith('0') ? phone.slice(1) : phone}`, '_blank');
  };

  const openEmail = () => {
    window.open(`mailto:${prospect.email}`, '_blank');
  };

  const handleConvertToStudent = async () => {
    if (window.confirm(t('confirmConvertToStudent'))) {
      await convertProspectToStudent(id!);
      navigate('/students');
    }
  };

  const handleConvertToLead = async () => {
    if (window.confirm(t('confirmConvertToLead'))) {
      await convertProspectToLead(id!);
      navigate('/leads');
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    setEditStatus(newStatus);
    await updateProspect(id!, { status: newStatus });
    setProspect({ ...prospect, status: newStatus });
  };

  const handleUploadDocument = async () => {
    if (!uploadFile || !id) return;
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('docType', uploadDocType);
    formData.append('notes', uploadNotes);
    formData.append('performedBy', userName);
    try {
      await uploadProspectDocument(id, formData);
      const res = await getProspectDocuments(id);
      setDocuments(res.data);
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadDocType('other');
      setUploadNotes('');
    } catch (err) {
      console.error('Upload failed', err);
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!id) return;
    try {
      await deleteProspectDocument(id, docId);
      setDocuments(documents.filter(d => d.id !== docId));
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  const handleAddCommunication = async () => {
    if (!id) return;
    await addProspectCommunication(id, commForm);
    const res = await getProspect(id);
    setProspect(res.data);
    setShowCommModal(false);
    setCommForm({ type: 'phone', subject: '', message: '', sentBy: userName, performedBy: userName });
  };

  const handleAddFollowUp = async () => {
    if (!id) return;
    await addProspectFollowUp(id, followUpForm);
    const res = await getProspect(id);
    setProspect(res.data);
    setShowFollowUpModal(false);
    setFollowUpForm({ handler: userName, action: '', nextStep: '', performedBy: userName });
  };

  const handleEditProspect = () => {
    setEditForm({
      firstName: prospect.firstName || '',
      lastName: prospect.lastName || '',
      firstNameAr: prospect.firstNameAr || '',
      lastNameAr: prospect.lastNameAr || '',
      nationalId: prospect.nationalId || '',
      phone: prospect.phone || '',
      email: prospect.email || '',
      address: prospect.address || '',
      city: prospect.city || '',
      dateOfBirth: prospect.dateOfBirth || '',
      requestedProgram: prospect.requestedProgram || '',
      branch: prospect.branch || '',
      academicYear: prospect.academicYear || '',
      source: prospect.source || '',
      programNotes: prospect.programNotes || '',
    });
    setIsEditing(true);
    setSuccessMsg('');
  };

  const handleSaveProspect = async () => {
    if (!id) return;
    try {
      await updateProspect(id, editForm);
      const res = await getProspect(id);
      setProspect(res.data);
      setIsEditing(false);
      setSuccessMsg('הנתונים נשמרו בהצלחה!');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      console.error('Save failed', err);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({});
  };

  const handleDeleteProspect = async () => {
    if (!id) return;
    if (window.confirm('האם אתה בטוח שברצונך למחוק?')) {
      try {
        await deleteProspect(id);
        navigate('/prospects');
      } catch (err) {
        console.error('Delete failed', err);
      }
    }
  };

  const formatPhoneForWhatsApp = (phone: string) => {
    const cleaned = phone.replace(/[^0-9]/g, '');
    return cleaned.startsWith('0') ? '972' + cleaned.slice(1) : cleaned;
  };

  const handleInitiateCall = async () => {
    if (!id || !prospect.phone) return;
    await initiateCall({ phoneNumber: prospect.phone, entityType: 'prospect', entityId: id, entityName: getName(), calledBy: userName });
    alert(lang === 'ar' ? 'بدأت المكالمة!' : 'השיחה התחילה!');
    getVoicenterCalls({ entityType: 'prospect', entityId: id }).then(res => setCallHistory(res.data)).catch(() => {});
  };

  const handleScheduleCall = async () => {
    if (!id || !prospect.phone) return;
    await scheduleCall({ phoneNumber: prospect.phone, entityType: 'prospect', entityId: id, entityName: getName(), calledBy: userName, ...scheduleCallForm });
    setShowScheduleCallModal(false);
    setScheduleCallForm({ scheduledDate: '', scheduledTime: '' });
    alert(lang === 'ar' ? 'تم جدولة المكالمة!' : 'השיחה תוזמנה!');
    getVoicenterCalls({ entityType: 'prospect', entityId: id }).then(res => setCallHistory(res.data)).catch(() => {});
  };

  const handleSendForSignature = async () => {
    if (!id) return;
    try {
      const res = await createSignatureRequest({
        entityType: 'prospect',
        entityId: id,
        entityName: getName(),
        documentName: signatureForm.documentName,
        message: signatureForm.message,
      });
      const signUrl = `${window.location.origin}/sign/${res.data.id}`;
      const whatsappPhone = formatPhoneForWhatsApp(prospect.phone);
      const text = encodeURIComponent(`${signatureForm.message}\n${t('signHere')}: ${signUrl}`);
      window.open(`https://wa.me/${whatsappPhone}?text=${text}`, '_blank');
      setShowSignatureModal(false);
      setSignatureForm({ documentName: '', message: '' });
      const sigRes = await getSignatures({ entityType: 'prospect', entityId: id });
      setSignatures(sigRes.data);
    } catch (err) {
      console.error('Signature request failed', err);
    }
  };

  const handleSendRegForm = async () => {
    if (!id || !regForm.courseName) return;
    try {
      const res = await createRegistration({
        entityType: 'prospect',
        entityId: id,
        recipientName: `${prospect.firstName} ${prospect.lastName}`,
        recipientEmail: prospect.email,
        recipientPhone: prospect.phone,
        courseName: regForm.courseName,
        tuitionAmount: Number(regForm.tuitionAmount) || 0,
        academicYear: regForm.academicYear,
        personalMessage: regForm.personalMessage,
        sendMethod: regForm.sendMethod,
        prefillData: {
          firstName: prospect.firstName || '',
          lastName: prospect.lastName || '',
          nationalId: prospect.nationalId || '',
          dateOfBirth: prospect.dateOfBirth || '',
          gender: '',
          phone: prospect.phone || '',
          email: prospect.email || '',
          city: prospect.city || '',
          zipCode: '',
        },
      });
      const regUrl = `${window.location.origin}/register/${res.data.id}`;
      if (regForm.sendMethod === 'whatsapp' || regForm.sendMethod === 'both') {
        const whatsappPhone = formatPhoneForWhatsApp(prospect.phone);
        const msg = encodeURIComponent(`${regForm.personalMessage ? regForm.personalMessage + '\n' : ''}טופס רישום למכללת מנארה:\n${regUrl}`);
        window.open(`https://wa.me/${whatsappPhone}?text=${msg}`, '_blank');
      }
      if (regForm.sendMethod === 'email' || regForm.sendMethod === 'both') {
        const subject = encodeURIComponent('טופס רישום - מכללת מנארה');
        const body = encodeURIComponent(`${regForm.personalMessage ? regForm.personalMessage + '\n\n' : ''}טופס רישום:\n${regUrl}`);
        window.open(`mailto:${prospect.email}?subject=${subject}&body=${body}`, '_blank');
      }
      setShowRegFormModal(false);
      setRegForm({ courseName: '', tuitionAmount: '', academicYear: '', personalMessage: '', sendMethod: 'both' });
      alert(lang === 'ar' ? 'تم إرسال نموذج التسجيل بنجاح!' : 'טופס הרישום נשלח בהצלחה!');
    } catch (err) {
      console.error('Send registration form failed', err);
    }
  };

  return (
    <div>
      <div className="page-section">
        <div className="detail-header">
          <div className="detail-avatar">{prospect.firstName[0]}</div>
          <div className="detail-info">
            <h2>{getName()}</h2>
            <p dir="ltr">{prospect.email} | {prospect.phone}</p>
            <span className={`badge ${statusColors[prospect.status]}`}>{getStatusText(prospect.status)}</span>
          </div>
          <div className="detail-actions">
            <button className="btn btn-sm" style={{ background: '#4CAF50', color: '#fff', border: 'none' }} onClick={handleInitiateCall}>📞 {t('callNow')}</button>
            <button className="btn btn-sm btn-whatsapp" onClick={openWhatsApp}>📱 {t('sendWhatsApp')}</button>
            <button className="btn btn-sm btn-email" onClick={openEmail}>📧 {t('sendEmail')}</button>
            <button className="btn btn-sm" style={{ background: '#E91E63', color: '#fff', border: 'none' }} onClick={() => setShowRegFormModal(true)}>📝 {lang === 'ar' ? 'إرسال نموذج تسجيل' : 'שלח טופס רישום'}</button>
            <button className="btn btn-sm btn-primary" onClick={handleConvertToStudent}>🎓 {t('convertToStudent')}</button>
            <button className="btn btn-sm btn-outline" onClick={handleConvertToLead}>🔙 {t('convertToLead')}</button>
            <Link to="/prospects" className="btn btn-sm btn-outline">← {t('back')}</Link>
          </div>
        </div>

        {/* Edit/Save/Cancel/Delete Buttons */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {!isEditing && (
            <button onClick={handleEditProspect} style={{ padding: '8px 20px', borderRadius: 6, border: 'none', color: '#fff', fontWeight: 600, cursor: 'pointer', background: '#2196F3' }}>
              עריכה
            </button>
          )}
          {isEditing && (
            <button onClick={handleSaveProspect} style={{ padding: '8px 20px', borderRadius: 6, border: 'none', color: '#fff', fontWeight: 600, cursor: 'pointer', background: '#4CAF50' }}>
              שמירה
            </button>
          )}
          {isEditing && (
            <button onClick={handleCancelEdit} style={{ padding: '8px 20px', borderRadius: 6, border: 'none', color: '#fff', fontWeight: 600, cursor: 'pointer', background: '#9E9E9E' }}>
              ביטול
            </button>
          )}
          <button onClick={handleDeleteProspect} style={{ padding: '8px 20px', borderRadius: 6, border: 'none', color: '#fff', fontWeight: 600, cursor: 'pointer', background: '#f44336' }}>
            מחיקה
          </button>
        </div>
        {successMsg && (
          <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '10px 16px', borderRadius: 6, marginBottom: 12, fontWeight: 500 }}>
            {successMsg}
          </div>
        )}

        {/* Status Change */}
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <label className="form-label" style={{ margin: 0 }}>{t('status')}:</label>
          <select
            className="form-select"
            style={{ width: 'auto', minWidth: 200 }}
            value={editStatus}
            onChange={e => handleStatusChange(e.target.value)}
          >
            <option value="initial_contact">{t('prospectStatuses.initial_contact')}</option>
            <option value="in_process">{t('prospectStatuses.in_process')}</option>
            <option value="documents_submitted">{t('prospectStatuses.documents_submitted')}</option>
            <option value="waiting_approval">{t('prospectStatuses.waiting_approval')}</option>
            <option value="approved_for_registration">{t('prospectStatuses.approved_for_registration')}</option>
            <option value="rejected">{t('prospectStatuses.rejected')}</option>
          </select>
        </div>

        <div className="tabs">
          <button className={`tab ${activeTab === 'info' ? 'active' : ''}`} onClick={() => setActiveTab('info')}>{t('personalInfo')}</button>
          <button className={`tab ${activeTab === 'program' ? 'active' : ''}`} onClick={() => setActiveTab('program')}>{t('requestedProgram')}</button>
          <button className={`tab ${activeTab === 'documents' ? 'active' : ''}`} onClick={() => setActiveTab('documents')}>📄 {t('documents')}</button>
          <button className={`tab ${activeTab === 'comm' ? 'active' : ''}`} onClick={() => setActiveTab('comm')}>{t('commHistory')}</button>
          <button className={`tab ${activeTab === 'calls' ? 'active' : ''}`} onClick={() => setActiveTab('calls')}>📞 {t('calls')}</button>
          <button className={`tab ${activeTab === 'followups' ? 'active' : ''}`} onClick={() => setActiveTab('followups')}>{t('followUpsTab')}</button>
          <button className={`tab ${activeTab === 'tasks' ? 'active' : ''}`} onClick={() => setActiveTab('tasks')}>📋 משימות</button>
        </div>

        {/* Personal Info Tab - View Mode */}
        {activeTab === 'info' && !isEditing && (
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{t('firstName')}</label>
              <div className="form-input" style={{ background: '#f8f9fa' }}>{prospect.firstName}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('lastName')}</label>
              <div className="form-input" style={{ background: '#f8f9fa' }}>{prospect.lastName}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('nationalId')}</label>
              <div className="form-input" style={{ background: '#f8f9fa' }}>{prospect.nationalId || '-'}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('phone')}</label>
              <div className="form-input" style={{ background: '#f8f9fa', direction: 'ltr', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}>
                {prospect.phone}
                <button onClick={handleInitiateCall} style={{ background: '#4CAF50', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 12 }}>📞 {t('callNow')}</button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('email')}</label>
              <div className="form-input" style={{ background: '#f8f9fa', direction: 'ltr', textAlign: 'left' }}>{prospect.email}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('address')}</label>
              <div className="form-input" style={{ background: '#f8f9fa' }}>{prospect.address || '-'}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('city')}</label>
              <div className="form-input" style={{ background: '#f8f9fa' }}>{prospect.city || '-'}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('dateOfBirth')}</label>
              <div className="form-input" style={{ background: '#f8f9fa' }}>{prospect.dateOfBirth || '-'}</div>
            </div>
          </div>
        )}

        {/* Personal Info Tab - Edit Mode */}
        {activeTab === 'info' && isEditing && (
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{t('firstName')}</label>
              <input className="form-input" value={editForm.firstName} onChange={e => setEditForm({...editForm, firstName: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('lastName')}</label>
              <input className="form-input" value={editForm.lastName} onChange={e => setEditForm({...editForm, lastName: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('firstName')} (ערבית)</label>
              <input className="form-input" value={editForm.firstNameAr} onChange={e => setEditForm({...editForm, firstNameAr: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('lastName')} (ערבית)</label>
              <input className="form-input" value={editForm.lastNameAr} onChange={e => setEditForm({...editForm, lastNameAr: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('nationalId')}</label>
              <input className="form-input" value={editForm.nationalId} onChange={e => setEditForm({...editForm, nationalId: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('phone')}</label>
              <input className="form-input" type="tel" value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('email')}</label>
              <input className="form-input" type="email" value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('address')}</label>
              <input className="form-input" value={editForm.address} onChange={e => setEditForm({...editForm, address: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('city')}</label>
              <input className="form-input" value={editForm.city} onChange={e => setEditForm({...editForm, city: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('dateOfBirth')}</label>
              <input className="form-input" type="date" value={editForm.dateOfBirth} onChange={e => setEditForm({...editForm, dateOfBirth: e.target.value})} />
            </div>
          </div>
        )}

        {/* Program Tab - View Mode */}
        {activeTab === 'program' && !isEditing && (
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{t('requestedProgram')}</label>
              <div className="form-input" style={{ background: '#f8f9fa' }}>{prospect.requestedProgram || '-'}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('branch')}</label>
              <div className="form-input" style={{ background: '#f8f9fa' }}>{prospect.branch || '-'}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('academicYear')}</label>
              <div className="form-input" style={{ background: '#f8f9fa' }}>{prospect.academicYear || '-'}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('source')}</label>
              <div className="form-input" style={{ background: '#f8f9fa' }}>{getSourceText(prospect.source)}</div>
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">{t('programNotes')}</label>
              <div className="form-input" style={{ background: '#f8f9fa', minHeight: 60 }}>{prospect.programNotes || '-'}</div>
            </div>
          </div>
        )}

        {/* Program Tab - Edit Mode */}
        {activeTab === 'program' && isEditing && (
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{t('requestedProgram')}</label>
              <input className="form-input" value={editForm.requestedProgram} onChange={e => setEditForm({...editForm, requestedProgram: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('branch')}</label>
              <input className="form-input" value={editForm.branch} onChange={e => setEditForm({...editForm, branch: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('academicYear')}</label>
              <input className="form-input" value={editForm.academicYear} onChange={e => setEditForm({...editForm, academicYear: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('source')}</label>
              <select className="form-select" value={editForm.source} onChange={e => setEditForm({...editForm, source: e.target.value})}>
                <option value="facebook">{t('sources.facebook')}</option>
                <option value="website">{t('sources.website')}</option>
                <option value="referral">{t('sources.referral')}</option>
                <option value="fair">{t('sources.fair')}</option>
                <option value="whatsapp">{t('sources.whatsapp')}</option>
                <option value="other">{t('sources.other')}</option>
              </select>
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">{t('programNotes')}</label>
              <textarea className="form-textarea" value={editForm.programNotes} onChange={e => setEditForm({...editForm, programNotes: e.target.value})} style={{ minHeight: 60 }} />
            </div>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div>
            <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={() => setShowUploadModal(true)}>📤 {t('uploadDocument')}</button>
              <button className="btn" style={{ background: '#7B1FA2', color: '#fff', border: 'none' }} onClick={() => setShowSignatureModal(true)}>✍️ {t('sendForSignature')}</button>
            </div>
            <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('fileName')}</th>
                  <th>{t('docType')}</th>
                  <th>{t('uploadDate')}</th>
                  <th>{t('notes')}</th>
                  <th>{t('performedBy')}</th>
                  <th>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {documents.map(doc => (
                  <tr key={doc.id} onContextMenu={e => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY, type: 'document', item: doc }); }}>
                    <td>{doc.fileName}</td>
                    <td><span className="badge badge-blue">{getDocTypeName(doc.docType)}</span></td>
                    <td>{new Date(doc.uploadDate).toLocaleDateString('he-IL')}</td>
                    <td>{doc.notes || '-'}</td>
                    <td>{doc.performedBy || '-'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <a
                          href={`${API_ORIGIN}/uploads/prospects/${id}/${doc.storedName}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline"
                        >👁️ {t('viewDoc')}</a>
                        <a
                          href={`${API_ORIGIN}/api/prospects/${id}/documents/${doc.id}/download`}
                          className="btn btn-sm"
                          style={{ backgroundColor: '#2196F3', color: '#fff', border: 'none' }}
                        >⬇️ {t('downloadDoc')}</a>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDeleteDocument(doc.id)}>🗑️ {t('deleteDoc')}</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {documents.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#999' }}>{t('noData')}</td></tr>}
              </tbody>
            </table>
            </div>

            {/* Signature Tracking */}
            {signatures.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <h4>✍️ {t('signatureTracking')}</h4>
                <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('document')}</th>
                      <th>{t('status')}</th>
                      <th>{t('sentDate')}</th>
                      <th>{t('signedDate')}</th>
                      <th>{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {signatures.map((sig: any) => (
                      <tr key={sig.id}>
                        <td>{sig.documentName}</td>
                        <td>
                          <span className={`badge ${sig.status === 'signed' ? 'badge-green' : 'badge-gold'}`}>
                            {sig.status === 'signed' ? t('signed') : t('pending')}
                          </span>
                        </td>
                        <td>{new Date(sig.createdAt).toLocaleDateString('he-IL')}</td>
                        <td>{sig.signedAt ? new Date(sig.signedAt).toLocaleDateString('he-IL') : '-'}</td>
                        <td>
                          {sig.status === 'pending' && (
                            <button className="btn btn-sm btn-whatsapp" onClick={() => {
                              const signUrl = `${window.location.origin}/sign/${sig.id}`;
                              const phone = formatPhoneForWhatsApp(prospect.phone);
                              const text = encodeURIComponent(`${t('signHere')}: ${signUrl}`);
                              window.open(`https://wa.me/${phone}?text=${text}`, '_blank');
                            }}>📱 {t('resend')}</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Communication Tab */}
        {activeTab === 'comm' && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <button className="btn btn-primary" onClick={() => { setCommForm({...commForm, sentBy: userName, performedBy: userName}); setShowCommModal(true); }}>+ {t('addCommunication')}</button>
            </div>
            <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('date')}</th>
                  <th>סוג</th>
                  <th>{t('subject')}</th>
                  <th>{t('message')}</th>
                  <th>{t('performedBy')}</th>
                </tr>
              </thead>
              <tbody>
                {(prospect.communications || []).map((c: any) => (
                  <tr key={c.id} onContextMenu={e => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY, type: 'comm', item: c }); }}>
                    <td>{c.date}</td>
                    <td>
                      <span className={`badge ${c.type === 'email' ? 'badge-blue' : c.type === 'whatsapp' ? 'badge-green' : 'badge-gold'}`}>
                        {c.type === 'email' ? '📧' : c.type === 'whatsapp' ? '📱' : '📞'} {c.type}
                      </span>
                    </td>
                    <td>{c.subject}</td>
                    <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.message}</td>
                    <td>{c.performedBy || c.sentBy || '-'}</td>
                  </tr>
                ))}
                {(!prospect.communications || prospect.communications.length === 0) &&
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: '#999' }}>{t('noData')}</td></tr>
                }
              </tbody>
            </table>
            </div>
          </div>
        )}

        {/* Calls Tab */}
        {activeTab === 'calls' && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button className="btn btn-sm" style={{ background: '#4CAF50', color: '#fff', border: 'none' }} onClick={handleInitiateCall}>📞 {t('callNow')}</button>
              <button className="btn btn-sm" style={{ background: '#FF9800', color: '#fff', border: 'none' }} onClick={() => setShowScheduleCallModal(true)}>🕐 {t('scheduleCallBtn')}</button>
            </div>
            <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('date')}</th>
                  <th>{t('callTime')}</th>
                  <th>{t('callDuration')}</th>
                  <th>{t('calledBy')}</th>
                  <th>{t('callDirection')}</th>
                  <th>{t('callStatus')}</th>
                  <th>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {callHistory.map((c: any) => (
                  <tr key={c.id}>
                    <td>{c.date}</td>
                    <td dir="ltr">{c.time}</td>
                    <td>{c.duration ? `${c.duration} ${t('sec')}` : '-'}</td>
                    <td>{c.calledBy || '-'}</td>
                    <td>
                      <span className={`badge ${c.direction === 'incoming' ? 'badge-blue' : 'badge-green'}`}>
                        {c.direction === 'incoming' ? `📥 ${t('incoming')}` : `📤 ${t('outgoing')}`}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${c.status === 'answered' ? 'badge-green' : c.status === 'no_answer' ? 'badge-red' : c.status === 'busy' ? 'badge-yellow' : c.status === 'scheduled' ? 'badge-blue' : 'badge-gray'}`}>
                        {t(c.status) || c.status}
                      </span>
                    </td>
                    <td>
                      {c.recordingUrl && (
                        <button className="btn btn-sm btn-outline" onClick={() => window.open(c.recordingUrl, '_blank')}>🎧 {t('listenRecording')}</button>
                      )}
                    </td>
                  </tr>
                ))}
                {callHistory.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: '#999' }}>{t('noData')}</td></tr>}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {/* Follow-ups Tab */}
        {activeTab === 'followups' && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <button className="btn btn-primary" onClick={() => { setFollowUpForm({...followUpForm, handler: userName, performedBy: userName}); setShowFollowUpModal(true); }}>+ {t('addFollowUp')}</button>
            </div>
            <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('date')}</th>
                  <th>{t('handler')}</th>
                  <th>{t('actionTaken')}</th>
                  <th>{t('nextStep')}</th>
                  <th>{t('performedBy')}</th>
                </tr>
              </thead>
              <tbody>
                {(prospect.followUps || []).map((fu: any) => (
                  <tr key={fu.id} onContextMenu={e => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY, type: 'followup', item: fu }); }}>
                    <td>{fu.date}</td>
                    <td><strong>{fu.handler}</strong></td>
                    <td>{fu.action}</td>
                    <td><span style={{ color: '#7B1FA2', fontWeight: 500 }}>{fu.nextStep}</span></td>
                    <td>{fu.performedBy || fu.handler || '-'}</td>
                  </tr>
                ))}
                {(!prospect.followUps || prospect.followUps.length === 0) &&
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: '#999' }}>{t('noData')}</td></tr>
                }
              </tbody>
            </table>
            </div>
          </div>
        )}

        {activeTab === 'tasks' && id && (
          <TasksTab entityType="prospect" entityId={id} documents={documents} />
        )}

        {/* Upload Document Modal */}
        {showUploadModal && (
          <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">📤 {t('uploadDocument')}</h3>
                <button className="modal-close" onClick={() => setShowUploadModal(false)}>×</button>
              </div>
              <div className="form-group">
                <label className="form-label">{t('docType')}</label>
                <select className="form-select" value={uploadDocType} onChange={e => setUploadDocType(e.target.value)}>
                  {docTypeKeys.map(key => (
                    <option key={key} value={key}>{getDocTypeName(key)}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t('fileName')}</label>
                <input type="file" className="form-input" onChange={e => setUploadFile(e.target.files?.[0] || null)} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('notes')}</label>
                <textarea className="form-textarea" value={uploadNotes} onChange={e => setUploadNotes(e.target.value)} />
              </div>
              <div className="modal-actions">
                <button className="btn btn-primary" onClick={handleUploadDocument} disabled={!uploadFile}>📤 {t('uploadDocument')}</button>
                <button className="btn btn-outline" onClick={() => setShowUploadModal(false)}>{t('cancel')}</button>
              </div>
            </div>
          </div>
        )}

        {/* Add Communication Modal */}
        {showCommModal && (
          <div className="modal-overlay" onClick={() => setShowCommModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">{t('addCommunication')}</h3>
                <button className="modal-close" onClick={() => setShowCommModal(false)}>×</button>
              </div>
              <div className="form-group">
                <label className="form-label">סוג תקשורת</label>
                <select className="form-select" value={commForm.type} onChange={e => setCommForm({...commForm, type: e.target.value})}>
                  <option value="phone">📞 טלפון</option>
                  <option value="email">📧 {t('email')}</option>
                  <option value="whatsapp">📱 WhatsApp</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t('subject')}</label>
                <input className="form-input" value={commForm.subject} onChange={e => setCommForm({...commForm, subject: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('message')}</label>
                <textarea className="form-textarea" value={commForm.message} onChange={e => setCommForm({...commForm, message: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('actionPerformer')}</label>
                <input className="form-input" value={commForm.performedBy} onChange={e => setCommForm({...commForm, performedBy: e.target.value, sentBy: e.target.value})} />
              </div>
              <div className="modal-actions">
                <button className="btn btn-primary" onClick={handleAddCommunication}>{t('save')}</button>
                <button className="btn btn-outline" onClick={() => setShowCommModal(false)}>{t('cancel')}</button>
              </div>
            </div>
          </div>
        )}

        {/* Add Follow-up Modal */}
        {showFollowUpModal && (
          <div className="modal-overlay" onClick={() => setShowFollowUpModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">{t('addFollowUp')}</h3>
                <button className="modal-close" onClick={() => setShowFollowUpModal(false)}>×</button>
              </div>
              <div className="form-group">
                <label className="form-label">{t('handler')}</label>
                <input className="form-input" value={followUpForm.handler} onChange={e => setFollowUpForm({...followUpForm, handler: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('actionTaken')}</label>
                <textarea className="form-textarea" value={followUpForm.action} onChange={e => setFollowUpForm({...followUpForm, action: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('nextStep')}</label>
                <textarea className="form-textarea" value={followUpForm.nextStep} onChange={e => setFollowUpForm({...followUpForm, nextStep: e.target.value})} />
              </div>
              <div className="modal-actions">
                <button className="btn btn-primary" onClick={handleAddFollowUp}>{t('save')}</button>
                <button className="btn btn-outline" onClick={() => setShowFollowUpModal(false)}>{t('cancel')}</button>
              </div>
            </div>
          </div>
        )}

        {/* Context Menu */}
        {ctxMenu && ctxMenu.type === 'document' && (
          <ContextMenu
            x={ctxMenu.x} y={ctxMenu.y}
            onClose={() => setCtxMenu(null)}
            items={[
              { label: 'צור משימה ממסמך זה', icon: '📋', onClick: () => setCtxTaskModal({ sourceType: 'document', sourceName: ctxMenu.item.fileName, sourceLink: `${API_ORIGIN}/uploads/prospects/${id}/${ctxMenu.item.storedName}` }), dividerAfter: true },
              { label: 'פתח מסמך', icon: '👁️', onClick: () => window.open(`${API_ORIGIN}/uploads/prospects/${id}/${ctxMenu.item.storedName}`, '_blank') },
              { label: 'הורד מסמך', icon: '⬇️', onClick: () => { window.location.href = `${API_ORIGIN}/api/prospects/${id}/documents/${ctxMenu.item.id}/download`; } },
            ]}
          />
        )}
        {ctxMenu && ctxMenu.type === 'comm' && (
          <ContextMenu
            x={ctxMenu.x} y={ctxMenu.y}
            onClose={() => setCtxMenu(null)}
            items={[
              { label: 'צור משימה מהודעה זו', icon: '📋', onClick: () => setCtxTaskModal({ sourceType: 'communication', sourceName: ctxMenu.item.subject || ctxMenu.item.message?.substring(0, 40) || 'הודעה' }) },
            ]}
          />
        )}
        {ctxMenu && ctxMenu.type === 'followup' && (
          <ContextMenu
            x={ctxMenu.x} y={ctxMenu.y}
            onClose={() => setCtxMenu(null)}
            items={[
              { label: 'צור משימה ממעקב זה', icon: '📋', onClick: () => setCtxTaskModal({ sourceType: 'communication', sourceName: ctxMenu.item.action || ctxMenu.item.nextStep || 'מעקב' }) },
            ]}
          />
        )}
        {/* Schedule Call Modal */}
        {showScheduleCallModal && (
          <div className="modal-overlay" onClick={() => setShowScheduleCallModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">🕐 {t('scheduleCallTitle')}</h3>
                <button className="modal-close" onClick={() => setShowScheduleCallModal(false)}>×</button>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('scheduledDate')}</label>
                  <input className="form-input" type="date" value={scheduleCallForm.scheduledDate} onChange={e => setScheduleCallForm({...scheduleCallForm, scheduledDate: e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('scheduledTime')}</label>
                  <input className="form-input" type="time" value={scheduleCallForm.scheduledTime} onChange={e => setScheduleCallForm({...scheduleCallForm, scheduledTime: e.target.value})} />
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn btn-primary" onClick={handleScheduleCall} disabled={!scheduleCallForm.scheduledDate || !scheduleCallForm.scheduledTime}>🕐 {t('scheduleCallBtn')}</button>
                <button className="btn btn-outline" onClick={() => setShowScheduleCallModal(false)}>{t('cancel')}</button>
              </div>
            </div>
          </div>
        )}

        {/* Send for Signature Modal */}
        {showSignatureModal && (
          <div className="modal-overlay" onClick={() => setShowSignatureModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">✍️ {t('sendForSignature')}</h3>
                <button className="modal-close" onClick={() => setShowSignatureModal(false)}>×</button>
              </div>
              <div className="form-group">
                <label className="form-label">{t('documentName')}</label>
                <input className="form-input" value={signatureForm.documentName} onChange={e => setSignatureForm({...signatureForm, documentName: e.target.value})} placeholder={t('documentNamePlaceholder')} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('message')}</label>
                <textarea className="form-textarea" value={signatureForm.message} onChange={e => setSignatureForm({...signatureForm, message: e.target.value})} placeholder={t('signatureMessagePlaceholder')} />
              </div>
              <div className="modal-actions">
                <button className="btn btn-primary" onClick={handleSendForSignature} disabled={!signatureForm.documentName}>✍️ {t('sendForSignature')}</button>
                <button className="btn btn-outline" onClick={() => setShowSignatureModal(false)}>{t('cancel')}</button>
              </div>
            </div>
          </div>
        )}

        {ctxTaskModal && id && (
          <TaskFromContextModal
            entityType="prospect"
            entityId={id}
            sourceType={ctxTaskModal.sourceType}
            sourceName={ctxTaskModal.sourceName}
            sourceLink={ctxTaskModal.sourceLink}
            onClose={() => setCtxTaskModal(null)}
            onCreated={() => setActiveTab('tasks')}
          />
        )}

        {/* Send Registration Form Modal */}
        {showRegFormModal && (
          <div className="modal-overlay" onClick={() => setShowRegFormModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 550 }}>
              <div className="modal-header">
                <h3 className="modal-title">📝 {lang === 'ar' ? 'إرسال نموذج تسجيل' : 'שלח טופס רישום'}</h3>
                <button className="modal-close" onClick={() => setShowRegFormModal(false)}>&times;</button>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{lang === 'ar' ? 'اسم المستلم' : 'שם הנמען'}</label>
                  <input className="form-input" value={`${prospect.firstName} ${prospect.lastName}`} readOnly style={{ background: '#f0f0f0' }} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('email')}</label>
                  <input className="form-input" value={prospect.email || ''} readOnly style={{ background: '#f0f0f0' }} dir="ltr" />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('phone')}</label>
                  <input className="form-input" value={prospect.phone || ''} readOnly style={{ background: '#f0f0f0' }} dir="ltr" />
                </div>
                <div className="form-group">
                  <label className="form-label">{lang === 'ar' ? 'المساق/البرنامج' : 'מסלול/קורס'} *</label>
                  <select className="form-select" value={regForm.courseName} onChange={e => setRegForm({...regForm, courseName: e.target.value})}>
                    <option value="">{lang === 'ar' ? 'اختر...' : 'בחר...'}</option>
                    {coursesList.map((c: any) => (
                      <option key={c.id} value={lang === 'ar' ? c.nameAr || c.name : c.name}>{lang === 'ar' ? c.nameAr || c.name : c.name}</option>
                    ))}
                    <option value={prospect.requestedProgram}>{prospect.requestedProgram} ({lang === 'ar' ? 'البرنامج المطلوب' : 'תכנית מבוקשת'})</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{lang === 'ar' ? 'الرسوم الدراسية' : 'שכר לימוד'}</label>
                  <input className="form-input" type="number" value={regForm.tuitionAmount}
                    onChange={e => setRegForm({...regForm, tuitionAmount: e.target.value})} placeholder="0" />
                </div>
                <div className="form-group">
                  <label className="form-label">{lang === 'ar' ? 'السنة الدراسية' : 'שנת לימודים'}</label>
                  <input className="form-input" value={regForm.academicYear}
                    onChange={e => setRegForm({...regForm, academicYear: e.target.value})} placeholder="2026" />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">{lang === 'ar' ? 'رسالة شخصية' : 'הודעה אישית'} ({lang === 'ar' ? 'اختياري' : 'אופציונלי'})</label>
                  <textarea className="form-textarea" value={regForm.personalMessage}
                    onChange={e => setRegForm({...regForm, personalMessage: e.target.value})} style={{ minHeight: 60 }} />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">{lang === 'ar' ? 'طريقة الإرسال' : 'אמצעי שליחה'}</label>
                  <div style={{ display: 'flex', gap: 10 }}>
                    {[{ key: 'email', label: lang === 'ar' ? 'بريد إلكتروني' : 'דואל' },
                      { key: 'whatsapp', label: lang === 'ar' ? 'واتساب' : 'ווטסאפ' },
                      { key: 'both', label: lang === 'ar' ? 'كلاهما' : 'שניהם' }
                    ].map(opt => (
                      <label key={opt.key} style={{
                        padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                        border: `2px solid ${regForm.sendMethod === opt.key ? '#7B1FA2' : '#ddd'}`,
                        background: regForm.sendMethod === opt.key ? '#f3e5f5' : '#fff',
                      }}>
                        <input type="radio" name="sendMethod" value={opt.key} checked={regForm.sendMethod === opt.key}
                          onChange={e => setRegForm({...regForm, sendMethod: e.target.value})} style={{ display: 'none' }} />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn btn-primary" onClick={handleSendRegForm} disabled={!regForm.courseName}>📝 {lang === 'ar' ? 'إرسال النموذج' : 'שלח טופס'}</button>
                <button className="btn btn-outline" onClick={() => setShowRegFormModal(false)}>{t('cancel')}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
